import { useMemo } from 'react';
import isEqual from 'lodash/isEqual';

import type { EventFieldConfig } from '../../../../config/tpoGenConfig';
import { normalizeValue, normalizeRecord } from '../../../../utils/formNormalize';

interface UseEventDirtyStateArgs {
    fieldConfigs: readonly EventFieldConfig[];
    formValues: Record<string, unknown>;
    fetchedFormValues: Record<string, unknown> | null;
    isEditMode: boolean;
    /** Fields currently disabled by the rule engine — excluded from validation and dirty tracking. */
    disabledFieldNames?: Set<string>;
    /** Fields currently hidden by the rule engine — excluded from validation and dirty tracking. */
    hiddenFieldNames?: Set<string>;
    /**
     * Create-mode only: field names the user has genuinely edited (via the Form's
     * `onValuesChange`, which reaction-driven `setFieldsValue` writes do NOT fire — see
     * `useRuleBoundForm`). Reactive autofills (e.g. Status defaulting to Draft on mount)
     * populate `formValues` without the user touching anything; gating the create-mode
     * dirty check on this set stops those defaults from being mistaken for real edits.
     * Ignored in edit mode, which instead diffs against `fetchedFormValues`.
     */
    touchedFieldNames?: Set<string>;
}

interface UseEventDirtyStateReturn {
    isValid: boolean;
    isDirty: boolean;
    canSave: boolean;
}

const useEventDirtyState = ({
    fieldConfigs,
    formValues,
    fetchedFormValues,
    isEditMode,
    disabledFieldNames,
    hiddenFieldNames,
    touchedFieldNames,
}: UseEventDirtyStateArgs): UseEventDirtyStateReturn => {
    const requiredFieldNames = useMemo(
        () => fieldConfigs.filter((f) => f.required && f.name).map((f) => f.name!),
        [fieldConfigs],
    );

    const isValid = useMemo(
        () =>
            requiredFieldNames.every((name) => {
                if (disabledFieldNames?.has(name) || hiddenFieldNames?.has(name)) return true;
                return normalizeValue(formValues[name]) !== null;
            }),
        [requiredFieldNames, formValues, disabledFieldNames, hiddenFieldNames],
    );

    const normalizedBaseline = useMemo(
        () => (fetchedFormValues ? normalizeRecord(fetchedFormValues) : null),
        [fetchedFormValues],
    );

    const isDirty = useMemo(() => {
        if (isEditMode) {
            if (!normalizedBaseline) {
                return false;
            }
            const allKeys = new Set([...Object.keys(normalizedBaseline), ...Object.keys(formValues)]);
            for (const key of allKeys) {
                if (disabledFieldNames?.has(key) || hiddenFieldNames?.has(key)) continue;
                const baseline = normalizedBaseline[key] ?? null;
                const current = normalizeValue(formValues[key]);
                if (!isEqual(baseline, current)) return true;
            }
            return false;
        }
        return Object.entries(formValues).some(([key, v]) => {
            if (disabledFieldNames?.has(key) || hiddenFieldNames?.has(key)) return false;
            // Untouched fields carry only rule-engine defaults (e.g. Status -> Draft),
            // not user intent — they don't count toward "unsaved changes".
            if (touchedFieldNames && !touchedFieldNames.has(key)) return false;
            return normalizeValue(v) !== null;
        });
    }, [isEditMode, formValues, normalizedBaseline, disabledFieldNames, hiddenFieldNames, touchedFieldNames]);

    const canSave = useMemo(
        () => (isEditMode ? isValid && isDirty : isValid),
        [isEditMode, isValid, isDirty],
    );

    return { isValid, isDirty, canSave };
};

export default useEventDirtyState;
